import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiLogOut, FiSettings, FiUsers, FiShoppingBag, FiTruck, FiDollarSign, 
  FiPackage, FiTrendingUp, FiFileText, FiCheck, FiX, FiEye, FiDownload,
  FiRefreshCw, FiMapPin, FiActivity, FiMenu, FiChevronRight, FiBook,
  FiStar, FiGift, FiPercent, FiEdit, FiCalendar, FiCpu, FiTag, FiShield
} from 'react-icons/fi';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import RealtimeAnalytics from '@/components/admin/RealtimeAnalytics';
import CerebroDashboard from '@/components/admin/CerebroDashboard';
import FreightCouponsAdmin from '@/components/admin/FreightCouponsAdmin';
import HandshakeSettings from '@/components/admin/HandshakeSettings';

const COLORS = ['#DC143C', '#1A1A1A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [stores, setStores] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeDeactivationReason, setStoreDeactivationReason] = useState('');
  
  // Novos estados para Promoções e ADS
  const [promotions, setPromotions] = useState([]);
  const [ads, setAds] = useState([]);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedStoreBilling, setSelectedStoreBilling] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [newPromotion, setNewPromotion] = useState({
    store_id: '',
    discount_type: 'percentage',
    discount_value: 10,
    applies_to: 'monthly_fee',
    end_date: '',
    reason: ''
  });

  const tabs = [
    { key: 'overview', label: 'Visão Geral', icon: FiTrendingUp },
    { key: 'cerebro', label: 'Cérebro', icon: FiCpu, highlight: true },
    { key: 'handshake', label: 'Handshake', icon: FiShield },
    { key: 'realtime', label: 'Tempo Real', icon: FiActivity },
    { key: 'coupons', label: 'Cupons', icon: FiTag },
    { key: 'users', label: 'Usuários', icon: FiUsers },
    { key: 'deliveries', label: 'Entregas', icon: FiMapPin },
    { key: 'stores', label: 'Lojas', icon: FiShoppingBag },
    { key: 'drivers', label: 'Entregadores', icon: FiTruck },
    { key: 'payments', label: 'Pagamentos', icon: FiDollarSign },
    { key: 'promotions', label: 'Promoções', icon: FiGift },
    { key: 'mediation', label: 'Mediação', icon: FiActivity },
    { key: 'pending', label: 'Pendentes', icon: FiFileText, badge: pendingDrivers.length },
    { key: 'export', label: 'Exportar', icon: FiDownload },
    { key: 'settings', label: 'Configurações', icon: FiSettings }
  ];

  // Estados para Mediação
  const [chatConversations, setChatConversations] = useState([]);
  const [selectedChatOrder, setSelectedChatOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatParticipants, setChatParticipants] = useState({});
  const [loadingChat, setLoadingChat] = useState(false);
  const [mediationMessage, setMediationMessage] = useState('');
  const [sendingMediation, setSendingMediation] = useState(false);
  const [chatSearch, setChatSearch] = useState('');

  const [driverPayments, setDriverPayments] = useState({ drivers: [], total_pending_amount: 0 });
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [processingPayments, setProcessingPayments] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, storesRes, driversRes, settingsRes, pendingRes, paymentsRes, revenueRes, promotionsRes, adsRes] = await Promise.all([
        api.get(`/admin/analytics?period=${period}`),
        api.get('/admin/stores'),
        api.get('/admin/drivers'),
        api.get('/admin/settings'),
        api.get('/admin/pending-drivers').catch(() => ({ data: [] })),
        api.get('/admin/driver-payments/pending').catch(() => ({ data: { drivers: [], total_pending_amount: 0 } })),
        api.get(`/admin/platform-revenue?period=${period}`).catch(() => ({ data: null })),
        api.get('/admin/promotions').catch(() => ({ data: [] })),
        api.get('/admin/ads').catch(() => ({ data: [] }))
      ]);
      setAnalytics(analyticsRes.data);
      setStores(storesRes.data);
      setDrivers(driversRes.data);
      setSettings(settingsRes.data);
      setPendingDrivers(pendingRes.data);
      setDriverPayments(paymentsRes.data);
      setPlatformRevenue(revenueRes.data);
      setPromotions(promotionsRes.data);
      setAds(adsRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [period, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleProcessWeeklyPayments = async () => {
    if (!window.confirm(`Confirmar pagamento semanal de R$ ${formatCurrency(driverPayments.total_pending_amount)} para ${driverPayments.drivers.length} entregadores?`)) {
      return;
    }
    
    setProcessingPayments(true);
    try {
      const response = await api.post('/admin/driver-payments/process-weekly', {
        driver_ids: [],
        notes: `Pagamento semanal - ${new Date().toLocaleDateString('pt-BR')}`
      });
      alert(`✅ ${response.data.message}\n\nTotal: R$ ${response.data.total_paid.toFixed(2)}\nEntregadores: ${response.data.drivers_paid}`);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao processar pagamentos');
    } finally {
      setProcessingPayments(false);
    }
  };

  const handleVerifyDriver = async (driverId, action) => {
    try {
      await api.post(`/admin/verify-driver/${driverId}?action=${action}`, {
        rejection_reason: action === 'reject' ? rejectionReason : null
      });
      alert(action === 'approve' ? 'Entregador aprovado!' : 'Entregador rejeitado');
      setSelectedDriver(null);
      setRejectionReason('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao processar');
    }
  };

  const handleStoreStatusChange = async (storeId, active) => {
    try {
      await api.put(`/admin/stores/${storeId}/status`, {
        active,
        reason: !active ? storeDeactivationReason : null
      });
      alert(active ? 'Loja reativada com sucesso!' : 'Loja desativada com sucesso!');
      setSelectedStore(null);
      setStoreDeactivationReason('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar status da loja');
    }
  };

  // Funções para Billing (Mensalidade)
  const handleOpenBillingModal = async (store) => {
    setSelectedStoreBilling(store);
    try {
      const response = await api.get(`/admin/stores/${store.id}/billing`);
      setBillingData(response.data);
      setShowBillingModal(true);
    } catch (error) {
      alert('Erro ao carregar dados de cobrança');
    }
  };

  const handleUpdateBilling = async (storeId, data) => {
    try {
      await api.put(`/admin/stores/${storeId}/billing`, data);
      alert('Configurações de cobrança atualizadas!');
      setShowBillingModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar cobrança');
    }
  };

  // Funções para Promoções
  const handleCreatePromotion = async () => {
    if (!newPromotion.store_id || !newPromotion.end_date) {
      alert('Selecione uma loja e defina a data de término');
      return;
    }
    try {
      await api.post('/admin/promotions', newPromotion);
      alert('Promoção criada com sucesso!');
      setShowPromotionModal(false);
      setNewPromotion({
        store_id: '',
        discount_type: 'percentage',
        discount_value: 10,
        applies_to: 'monthly_fee',
        end_date: '',
        reason: ''
      });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao criar promoção');
    }
  };

  const handleCancelPromotion = async (promotionId) => {
    if (!window.confirm('Deseja cancelar esta promoção?')) return;
    try {
      await api.delete(`/admin/promotions/${promotionId}`);
      alert('Promoção cancelada!');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao cancelar promoção');
    }
  };

  // Funções de Mediação
  const loadChatConversations = async (search = '') => {
    try {
      const response = await api.get(`/admin/chat/conversations?limit=100${search ? `&search=${search}` : ''}`);
      setChatConversations(response.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    }
  };

  const loadChatMessages = async (orderId) => {
    setLoadingChat(true);
    try {
      const response = await api.get(`/admin/chat/messages/${orderId}`);
      setChatMessages(response.data.messages);
      setChatParticipants(response.data.participants);
      setSelectedChatOrder(orderId);
    } catch (error) {
      alert('Erro ao carregar mensagens');
    } finally {
      setLoadingChat(false);
    }
  };

  const sendMediationMessage = async () => {
    if (!mediationMessage.trim() || !selectedChatOrder) return;
    
    setSendingMediation(true);
    try {
      await api.post(`/admin/chat/send-message/${selectedChatOrder}?content=${encodeURIComponent(mediationMessage)}`);
      setMediationMessage('');
      loadChatMessages(selectedChatOrder); // Refresh messages
      alert('Mensagem de mediação enviada!');
    } catch (error) {
      alert('Erro ao enviar mensagem');
    } finally {
      setSendingMediation(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'mediation') {
      loadChatConversations();
    }
  }, [activeTab]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      alert('Configurações salvas!');
    } catch (error) {
      alert('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (reportType) => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/export/${reportType}?period=${period}`);
      const ws = XLSX.utils.json_to_sheet(response.data.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, reportType);
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([excelBuffer]), `barueriflash_${reportType}_${period}.xlsx`);
    } catch (error) {
      alert('Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  const selectTab = (key) => {
    setActiveTab(key);
    setMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Carregando painel..." />
      </div>
    );
  }

  const statusData = analytics?.orders?.status_breakdown ? 
    Object.entries(analytics.orders.status_breakdown).map(([name, value]) => ({
      name: name === 'entregue' ? 'Entregue' : name === 'pendente' ? 'Pendente' : name === 'em_preparo' ? 'Preparando' : name,
      value
    })) : [];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMenuOpen(false)} />
      )}

      {/* Sidebar - Mobile Drawer / Desktop Fixed */}
      <aside className={`fixed top-0 left-0 h-full w-72 bg-gray-800 z-50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
        menuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg width="32" height="40" viewBox="0 0 80 100">
              <defs>
                <linearGradient id="boltGradSide" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFED4A" />
                  <stop offset="50%" stopColor="#FFD700" />
                  <stop offset="100%" stopColor="#FFA500" />
                </linearGradient>
              </defs>
              <path d="M45 0 L25 40 L40 40 L20 70 L35 70 L10 100 L50 60 L35 60 L55 30 L40 30 L60 0 Z" fill="url(#boltGradSide)"/>
            </svg>
            <div>
              <h1 className="font-bold text-lg">
                <span className="text-white">BARUERI</span>
                <span className="text-[#FFD700]">FLASH</span>
              </h1>
              <p className="text-xs text-gray-400">Painel Admin</p>
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <div className="p-4 border-b border-gray-700 flex-shrink-0">
          <label className="text-xs text-gray-400 block mb-2">Período</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm"
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="365d">Último ano</option>
          </select>
        </div>

        {/* Scrollable Navigation + Footer */}
        <div className="flex-1 overflow-y-auto">
          {/* Navigation */}
          <nav className="p-2">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => selectTab(tab.key)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl mb-1 transition-all ${
                  activeTab === tab.key 
                    ? 'bg-[#DC143C] text-white' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <tab.icon size={18} />
                  <span className="text-sm font-medium">{tab.label}</span>
                </div>
                {tab.badge > 0 && (
                  <span className="bg-yellow-500 text-gray-900 text-xs font-bold px-2 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Sidebar Footer - Inside scroll area */}
          <div className="p-4 border-t border-gray-700 space-y-2 mt-auto">
            <button
              onClick={() => navigate('/admin/relatorio')}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition-colors"
            >
              <FiBook size={18} />
              <span className="text-sm">Relatório do Sistema</span>
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-xl transition-colors"
              data-testid="admin-logout-btn"
            >
              <FiLogOut size={18} />
              <span className="text-sm font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-72">
        {/* Mobile Header */}
        <header className="sticky top-0 bg-gray-800 border-b border-gray-700 z-30 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button onClick={() => setMenuOpen(true)} className="p-2 hover:bg-gray-700 rounded-lg">
              <FiMenu size={24} />
            </button>
            <h1 className="font-bold">
              <span className="text-white">BARUERI</span>
              <span className="text-[#FFD700]">FLASH</span>
            </h1>
            <button onClick={loadData} className="p-2 hover:bg-gray-700 rounded-lg">
              <FiRefreshCw size={20} />
            </button>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between px-6 py-4 bg-gray-800 border-b border-gray-700">
          <h2 className="text-xl font-semibold">{tabs.find(t => t.key === activeTab)?.label}</h2>
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors">
            <FiRefreshCw size={16} /> Atualizar
          </button>
        </header>

        {/* Content */}
        <main className="p-4 lg:p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && analytics && (
            <div className="space-y-4 lg:space-y-6 animate-fadeIn">
              {/* KPIs - 2x2 on mobile, 4 columns on desktop */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                <div className="bg-gradient-to-br from-[#DC143C] to-[#8B0000] rounded-xl p-4">
                  <FiDollarSign className="text-xl lg:text-2xl text-white/80 mb-2" />
                  <p className="text-xl lg:text-3xl font-bold">{formatCurrency(analytics.revenue.total)}</p>
                  <p className="text-white/70 text-xs lg:text-sm">Receita Total</p>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4">
                  <FiUsers className="text-xl lg:text-2xl text-white/80 mb-2" />
                  <p className="text-xl lg:text-3xl font-bold">{analytics.users.total_clients}</p>
                  <p className="text-white/70 text-xs lg:text-sm">Compradores</p>
                </div>
                <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-4">
                  <FiPackage className="text-xl lg:text-2xl text-white/80 mb-2" />
                  <p className="text-xl lg:text-3xl font-bold">{analytics.orders.delivered}</p>
                  <p className="text-white/70 text-xs lg:text-sm">Pedidos</p>
                </div>
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-4">
                  <FiMapPin className="text-xl lg:text-2xl text-white/80 mb-2" />
                  <p className="text-xl lg:text-3xl font-bold">{analytics.deliveries.total_km}</p>
                  <p className="text-white/70 text-xs lg:text-sm">KM Rodados</p>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                <h2 className="text-base lg:text-lg font-semibold mb-4 flex items-center gap-2">
                  <FiTrendingUp className="text-[#DC143C]" /> Faturamento Diário
                </h2>
                <div className="h-48 lg:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.revenue.daily}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#DC143C" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#DC143C" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 10 }} tickFormatter={(v) => `R$${v}`} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '12px' }} />
                      <Area type="monotone" dataKey="revenue" stroke="#DC143C" fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue Breakdown - Stacked on mobile */}
              <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                  <h2 className="text-base lg:text-lg font-semibold mb-4">Status dos Pedidos</h2>
                  <div className="h-48 lg:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%" cy="50%"
                          innerRadius={40} outerRadius={70}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                          labelLine={false}
                        >
                          {statusData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                  <h2 className="text-base lg:text-lg font-semibold mb-4">Receita</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-gray-300 text-sm">Comissões</span>
                      <span className="font-bold text-green-400">{formatCurrency(analytics.revenue.platform_fees)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-gray-300 text-sm">Taxa Entregas</span>
                      <span className="font-bold text-blue-400">{formatCurrency(analytics.revenue.delivery_fees)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-[#DC143C]/20 rounded-lg border border-[#DC143C]/50">
                      <span className="text-white font-medium text-sm">Total</span>
                      <span className="font-bold text-[#DC143C] text-lg">{formatCurrency(analytics.revenue.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Lists */}
              <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                  <h2 className="text-base lg:text-lg font-semibold mb-4 flex items-center gap-2">
                    <FiShoppingBag className="text-blue-400" /> Top Lojas
                  </h2>
                  <div className="space-y-2">
                    {analytics.top_stores.slice(0, 5).map((store, idx) => (
                      <div key={store.id} className="flex items-center justify-between p-2 lg:p-3 bg-gray-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 lg:w-6 lg:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-gray-600'
                          }`}>{idx + 1}</span>
                          <span className="text-sm truncate max-w-[120px] lg:max-w-none">{store.name}</span>
                        </div>
                        <span className="font-bold text-green-400 text-sm">{formatCurrency(store.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                  <h2 className="text-base lg:text-lg font-semibold mb-4 flex items-center gap-2">
                    <FiTruck className="text-orange-400" /> Top Entregadores
                  </h2>
                  <div className="space-y-2">
                    {analytics.top_drivers.slice(0, 5).map((driver, idx) => (
                      <div key={driver.id} className="flex items-center justify-between p-2 lg:p-3 bg-gray-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 lg:w-6 lg:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-gray-600'
                          }`}>{idx + 1}</span>
                          <div>
                            <span className="text-sm block truncate max-w-[100px] lg:max-w-none">{driver.name}</span>
                            <span className="text-xs text-gray-400">{driver.deliveries} entregas</span>
                          </div>
                        </div>
                        <span className="text-xs lg:text-sm text-gray-300">{driver.total_km} km</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cérebro Inteligente Tab */}
          {activeTab === 'cerebro' && (
            <div className="animate-fadeIn">
              <CerebroDashboard />
            </div>
          )}

          {/* Handshake Settings Tab */}
          {activeTab === 'handshake' && (
            <div className="animate-fadeIn">
              <HandshakeSettings />
            </div>
          )}

          {/* Realtime Analytics Tab */}
          {activeTab === 'realtime' && (
            <div className="animate-fadeIn">
              <RealtimeAnalytics />
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === 'coupons' && (
            <div className="animate-fadeIn">
              <FreightCouponsAdmin />
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && analytics && (
            <div className="space-y-4 lg:space-y-6 animate-fadeIn">
              <div className="grid grid-cols-3 gap-3 lg:gap-4">
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <FiUsers className="mx-auto text-2xl text-blue-400 mb-2" />
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.users.total_clients}</p>
                  <p className="text-gray-400 text-xs lg:text-sm">Compradores</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <FiShoppingBag className="mx-auto text-2xl text-purple-400 mb-2" />
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.users.total_lojistas}</p>
                  <p className="text-gray-400 text-xs lg:text-sm">Lojistas</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <FiTruck className="mx-auto text-2xl text-orange-400 mb-2" />
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.users.total_entregadores}</p>
                  <p className="text-gray-400 text-xs lg:text-sm">Entregadores</p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                <h2 className="text-base lg:text-lg font-semibold mb-4">Novos Cadastros no Período</h2>
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: 'Compradores', total: analytics.users.total_clients, novos: analytics.users.new_clients },
                      { name: 'Lojistas', total: analytics.users.total_lojistas, novos: analytics.users.new_lojistas },
                      { name: 'Entregadores', total: analytics.users.total_entregadores, novos: analytics.users.new_entregadores }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', fontSize: '12px' }} />
                      <Bar dataKey="novos" name="Novos" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Deliveries Tab */}
          {activeTab === 'deliveries' && analytics && (
            <div className="space-y-4 lg:space-y-6 animate-fadeIn">
              <div className="grid grid-cols-3 gap-3 lg:gap-4">
                <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-4 text-center">
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.deliveries.total_deliveries}</p>
                  <p className="text-white/70 text-xs lg:text-sm">Entregas</p>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 text-center">
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.deliveries.total_km}</p>
                  <p className="text-white/70 text-xs lg:text-sm">KM Total</p>
                </div>
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-4 text-center">
                  <p className="text-2xl lg:text-4xl font-bold">{analytics.deliveries.avg_km_per_delivery}</p>
                  <p className="text-white/70 text-xs lg:text-sm">KM Médio</p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4 lg:p-6">
                <h2 className="text-base lg:text-lg font-semibold mb-4">Ranking de Entregadores por KM</h2>
                <div className="space-y-2">
                  {analytics.top_drivers.map((driver, idx) => (
                    <div key={driver.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-gray-600'
                        }`}>{idx + 1}</span>
                        <div>
                          <p className="font-medium text-sm">{driver.name}</p>
                          <p className="text-xs text-gray-400">{driver.deliveries} entregas</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-400 font-bold text-sm">{driver.total_km} km</p>
                        <p className="text-xs text-gray-400">{driver.avg_km} km/entrega</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Stores Tab */}
          {activeTab === 'stores' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h2 className="text-base lg:text-lg font-semibold">Lojas ({stores.length})</h2>
                <button
                  onClick={() => handleExport('stores')}
                  disabled={exporting}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  <FiDownload size={16} /> Excel
                </button>
              </div>
              
              {/* Store Status Modal */}
              {selectedStore && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                  <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-lg font-semibold mb-4">
                      {selectedStore.is_active ? 'Desativar' : 'Reativar'} Loja
                    </h3>
                    <p className="text-gray-300 mb-4">
                      <strong>{selectedStore.name}</strong>
                      <br />
                      <span className="text-sm text-gray-400">Proprietário: {selectedStore.owner_name}</span>
                    </p>
                    
                    {selectedStore.is_active && (
                      <div className="mb-4">
                        <label className="block text-sm text-gray-400 mb-2">Motivo da desativação:</label>
                        <select 
                          value={storeDeactivationReason}
                          onChange={(e) => setStoreDeactivationReason(e.target.value)}
                          className="w-full p-3 bg-gray-700 rounded-lg text-white"
                        >
                          <option value="">Selecione um motivo</option>
                          <option value="non_payment">Falta de pagamento</option>
                          <option value="bad_faith">Má-fé / Fraude</option>
                          <option value="terms_violation">Violação dos termos</option>
                          <option value="inactivity">Inatividade prolongada</option>
                          <option value="other">Outro motivo</option>
                        </select>
                      </div>
                    )}
                    
                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setSelectedStore(null)}
                        className="flex-1 py-3 bg-gray-700 rounded-lg hover:bg-gray-600"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleStoreStatusChange(selectedStore.id, !selectedStore.is_active)}
                        disabled={selectedStore.is_active && !storeDeactivationReason}
                        className={`flex-1 py-3 rounded-lg font-medium ${
                          selectedStore.is_active 
                            ? 'bg-red-600 hover:bg-red-700 disabled:opacity-50' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {selectedStore.is_active ? 'Desativar Loja' : 'Reativar Loja'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mobile: Cards */}
              <div className="lg:hidden space-y-3">
                {stores.map(store => (
                  <div key={store.id} className={`bg-gray-800 rounded-xl p-4 ${!store.is_active ? 'border-2 border-red-500/50 opacity-75' : ''}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold">{store.name}</h3>
                        {!store.is_active && (
                          <span className="text-xs text-red-400">Desativada - {store.deactivation_reason || 'Sem motivo'}</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          store.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}>
                          {store.is_active ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400">{store.owner_name}</p>
                    <div className="flex justify-between mt-3 pt-3 border-t border-gray-700">
                      <span className="text-sm text-gray-400">{store.orders_count} pedidos</span>
                      <span className="font-bold text-green-400">{formatCurrency(store.monthly_sales)}</span>
                    </div>
                    <button
                      onClick={() => { setSelectedStore(store); setStoreDeactivationReason(''); }}
                      className={`w-full mt-3 py-2 rounded-lg text-sm font-medium ${
                        store.is_active 
                          ? 'bg-red-900/50 text-red-300 hover:bg-red-900' 
                          : 'bg-green-900/50 text-green-300 hover:bg-green-900'
                      }`}
                    >
                      {store.is_active ? 'Desativar Loja' : 'Reativar Loja'}
                    </button>
                  </div>
                ))}
              </div>

              {/* Desktop: Table */}
              <div className="hidden lg:block bg-gray-800 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-gray-300">Loja</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-300">Proprietário</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-300">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-300">MP</th>
                      <th className="text-right p-4 text-sm font-medium text-gray-300">Pedidos</th>
                      <th className="text-right p-4 text-sm font-medium text-gray-300">Vendas</th>
                      <th className="text-center p-4 text-sm font-medium text-gray-300">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {stores.map(store => (
                      <tr key={store.id} className={`hover:bg-gray-700/50 ${!store.is_active ? 'bg-red-900/10' : ''}`}>
                        <td className="p-4">
                          <div>
                            <span className="font-medium">{store.name}</span>
                            {!store.is_active && store.deactivation_reason && (
                              <p className="text-xs text-red-400 mt-1">
                                Motivo: {store.deactivation_reason === 'non_payment' ? 'Falta de pagamento' : 
                                         store.deactivation_reason === 'bad_faith' ? 'Má-fé' :
                                         store.deactivation_reason === 'terms_violation' ? 'Violação dos termos' :
                                         store.deactivation_reason === 'inactivity' ? 'Inatividade' : store.deactivation_reason}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-gray-400">{store.owner_name}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            store.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                          }`}>
                            {store.is_active ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            store.mp_configured ? 'bg-blue-900 text-blue-300' : 'bg-yellow-900 text-yellow-300'
                          }`}>
                            {store.mp_configured ? 'Ativo' : 'Pendente'}
                          </span>
                        </td>
                        <td className="p-4 text-right">{store.orders_count}</td>
                        <td className="p-4 text-right text-green-400">{formatCurrency(store.monthly_sales)}</td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => { setSelectedStore(store); setStoreDeactivationReason(''); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                              store.is_active 
                                ? 'bg-red-900/50 text-red-300 hover:bg-red-900' 
                                : 'bg-green-900/50 text-green-300 hover:bg-green-900'
                            }`}
                          >
                            {store.is_active ? 'Desativar' : 'Reativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Drivers Tab */}
          {activeTab === 'drivers' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h2 className="text-base lg:text-lg font-semibold">Entregadores ({drivers.length})</h2>
                <button
                  onClick={() => handleExport('drivers')}
                  disabled={exporting}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  <FiDownload size={16} /> Excel
                </button>
              </div>
              
              <div className="space-y-3">
                {drivers.map(driver => (
                  <div key={driver.id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{driver.name}</h3>
                        <p className="text-sm text-gray-400">{driver.email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        driver.status_online ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {driver.status_online ? '🟢 Online' : '⚫ Offline'}
                      </span>
                    </div>
                    <div className="flex justify-between mt-3 pt-3 border-t border-gray-700 text-sm">
                      <span className="text-gray-400">{driver.total_deliveries} entregas</span>
                      <span className="text-yellow-400 font-bold">{formatCurrency(driver.wallet_balance)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments Tab - Weekly Driver Payouts */}
          {activeTab === 'payments' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h2 className="text-base lg:text-lg font-semibold">Pagamentos Semanais</h2>
                <button
                  onClick={() => loadData()}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiRefreshCw size={18} />
                </button>
              </div>

              {/* Platform Revenue Summary */}
              {platformRevenue && (
                <div className="bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-xl p-4 border border-green-700/30">
                  <h3 className="font-semibold text-green-400 mb-3">Receita da Plataforma ({period})</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-400">Total Receita</p>
                      <p className="text-xl font-bold text-green-400">{formatCurrency(platformRevenue.total_revenue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Comissões</p>
                      <p className="text-lg font-semibold">{formatCurrency(platformRevenue.breakdown?.commissions || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Taxa Entrega (Plataforma)</p>
                      <p className="text-lg font-semibold">{formatCurrency(platformRevenue.breakdown?.platform_delivery_cut || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Pendente Entregadores</p>
                      <p className="text-lg font-semibold text-yellow-400">{formatCurrency(platformRevenue.breakdown?.driver_payments_pending || 0)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending Driver Payments */}
              <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold">Pagamentos Pendentes para Entregadores</h3>
                    <p className="text-sm text-gray-400">{driverPayments.drivers?.length || 0} entregadores aguardando</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Total a Pagar</p>
                    <p className="text-2xl font-bold text-yellow-400">{formatCurrency(driverPayments.total_pending_amount || 0)}</p>
                  </div>
                </div>

                {driverPayments.drivers?.length > 0 ? (
                  <>
                    <div className="overflow-x-auto mb-4">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-700">
                          <tr>
                            <th className="text-left p-3">Entregador</th>
                            <th className="text-left p-3">Chave PIX</th>
                            <th className="text-right p-3">Entregas</th>
                            <th className="text-right p-3">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {driverPayments.drivers.map(driver => (
                            <tr key={driver.driver_id} className="hover:bg-gray-700/50">
                              <td className="p-3">
                                <p className="font-medium">{driver.driver_name}</p>
                                <p className="text-xs text-gray-400">{driver.driver_email}</p>
                              </td>
                              <td className="p-3">
                                <code className="text-xs bg-gray-700 px-2 py-1 rounded">
                                  {driver.driver_pix_key || driver.driver_email || 'Não informada'}
                                </code>
                              </td>
                              <td className="p-3 text-right">{driver.orders_count}</td>
                              <td className="p-3 text-right font-bold text-green-400">
                                {formatCurrency(driver.total_pending)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleProcessWeeklyPayments}
                        disabled={processingPayments}
                        className="flex-1 py-3 bg-green-600 hover:bg-green-700 rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {processingPayments ? (
                          <>
                            <FiRefreshCw className="animate-spin" />
                            Processando...
                          </>
                        ) : (
                          <>
                            <FiDollarSign />
                            Liberar Pagamento Semanal ({formatCurrency(driverPayments.total_pending_amount)})
                          </>
                        )}
                      </button>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/driver-payments/export-csv`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        <FiDownload />
                        CSV
                      </a>
                    </div>
                    
                    <p className="text-xs text-gray-500 text-center mt-3">
                      Clique em "Liberar" para processar todos os pagamentos automaticamente.
                      O sistema notificará cada entregador.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <FiDollarSign className="mx-auto text-4xl text-gray-600 mb-3" />
                    <p className="text-gray-400">Nenhum pagamento pendente</p>
                    <p className="text-sm text-gray-500">Os pagamentos aparecem aqui após entregas concluídas</p>
                  </div>
                )}
              </div>

              {/* How it works */}
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FiActivity /> Como funciona o Split de Pagamento
                </h4>
                <div className="grid md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center mb-2 text-blue-300">1</div>
                    <p className="text-gray-300">Cliente paga via PIX</p>
                    <p className="text-xs text-gray-500">Total: produtos + frete</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="w-8 h-8 bg-purple-900 rounded-full flex items-center justify-center mb-2 text-purple-300">2</div>
                    <p className="text-gray-300">Lojista recebe</p>
                    <p className="text-xs text-gray-500">Produtos - comissão</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="w-8 h-8 bg-red-900 rounded-full flex items-center justify-center mb-2 text-red-300">3</div>
                    <p className="text-gray-300">Plataforma recebe</p>
                    <p className="text-xs text-gray-500">Comissão + frete (via application_fee)</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="w-8 h-8 bg-green-900 rounded-full flex items-center justify-center mb-2 text-green-300">4</div>
                    <p className="text-gray-300">Entregador recebe</p>
                    <p className="text-xs text-gray-500">R$10/entrega (semanal)</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pending Drivers Tab */}
          {activeTab === 'pending' && (
            <div className="space-y-4 animate-fadeIn">
              <h2 className="text-base lg:text-lg font-semibold">Pendentes ({pendingDrivers.length})</h2>
              
              {pendingDrivers.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center">
                  <FiFileText className="mx-auto text-4xl text-gray-600 mb-3" />
                  <p className="text-gray-400">Nenhum entregador pendente</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingDrivers.map(driver => (
                    <div key={driver.id} className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold">{driver.name}</h3>
                          <p className="text-sm text-gray-400">{driver.email}</p>
                        </div>
                        <span className="px-2 py-1 bg-yellow-900 text-yellow-300 rounded-full text-xs">Pendente</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                        <div>
                          <p className="text-gray-400">CNH</p>
                          <p className="font-medium">{driver.cnh_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Placa</p>
                          <p className="font-medium">{driver.vehicle_plate || 'N/A'}</p>
                        </div>
                      </div>
                      
                      {(driver.cnh_photo || driver.vehicle_photo) && (
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {driver.cnh_photo && (
                            <a href={driver.cnh_photo} target="_blank" rel="noopener noreferrer">
                              <img src={driver.cnh_photo} alt="CNH" className="w-full h-24 object-cover rounded-lg border border-gray-700" />
                            </a>
                          )}
                          {driver.vehicle_photo && (
                            <a href={driver.vehicle_photo} target="_blank" rel="noopener noreferrer">
                              <img src={driver.vehicle_photo} alt="Veículo" className="w-full h-24 object-cover rounded-lg border border-gray-700" />
                            </a>
                          )}
                        </div>
                      )}
                      
                      {selectedDriver === driver.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Motivo da rejeição (obrigatório)"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleVerifyDriver(driver.id, 'approve')}
                              className="flex-1 py-2 bg-green-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                            >
                              <FiCheck /> Aprovar
                            </button>
                            <button
                              onClick={() => rejectionReason.trim() ? handleVerifyDriver(driver.id, 'reject') : alert('Informe o motivo')}
                              className="flex-1 py-2 bg-red-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                            >
                              <FiX /> Rejeitar
                            </button>
                            <button
                              onClick={() => { setSelectedDriver(null); setRejectionReason(''); }}
                              className="px-3 py-2 bg-gray-700 rounded-lg text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedDriver(driver.id)}
                          className="w-full py-2 bg-[#DC143C] rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <FiEye /> Verificar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-4 animate-fadeIn">
              <h2 className="text-base lg:text-lg font-semibold">Exportar Relatórios</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { type: 'revenue', title: 'Faturamento', icon: FiDollarSign, color: 'from-green-600 to-green-800' },
                  { type: 'orders', title: 'Pedidos', icon: FiPackage, color: 'from-blue-600 to-blue-800' },
                  { type: 'users', title: 'Compradores', icon: FiUsers, color: 'from-purple-600 to-purple-800' },
                  { type: 'stores', title: 'Lojas', icon: FiShoppingBag, color: 'from-orange-600 to-orange-800' },
                  { type: 'drivers', title: 'Entregadores', icon: FiTruck, color: 'from-red-600 to-red-800' }
                ].map(report => (
                  <div key={report.type} className={`bg-gradient-to-br ${report.color} rounded-xl p-4 lg:p-6`}>
                    <report.icon className="text-2xl text-white/80 mb-2" />
                    <h3 className="font-semibold">{report.title}</h3>
                    <button
                      onClick={() => handleExport(report.type)}
                      disabled={exporting}
                      className="mt-4 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <FiDownload /> Baixar Excel
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Promotions Tab */}
          {activeTab === 'promotions' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Promoções e ADS</h2>
                <button
                  onClick={() => setShowPromotionModal(true)}
                  className="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 text-sm flex items-center gap-2"
                >
                  <FiGift /> Nova Promoção
                </button>
              </div>

              {/* Promoções Ativas */}
              <div className="bg-gray-800 rounded-xl p-4">
                <h3 className="font-medium text-green-400 mb-4 flex items-center gap-2">
                  <FiGift /> Promoções Ativas ({promotions.filter(p => p.active).length})
                </h3>
                {promotions.filter(p => p.active).length === 0 ? (
                  <p className="text-gray-400 text-sm">Nenhuma promoção ativa</p>
                ) : (
                  <div className="space-y-3">
                    {promotions.filter(p => p.active).map(promo => (
                      <div key={promo.id} className="bg-gray-700/50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{promo.store_name}</p>
                          <p className="text-sm text-gray-400">
                            {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `R$ ${promo.discount_value}`} 
                            {' '}de desconto na {promo.applies_to === 'monthly_fee' ? 'mensalidade' : promo.applies_to === 'commission' ? 'comissão' : 'mensalidade e comissão'}
                          </p>
                          <p className="text-xs text-gray-500">Até {new Date(promo.end_date).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <button
                          onClick={() => handleCancelPromotion(promo.id)}
                          className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/40"
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ADS Ativos */}
              <div className="bg-gray-800 rounded-xl p-4">
                <h3 className="font-medium text-yellow-400 mb-4 flex items-center gap-2">
                  <FiStar /> Produtos em Destaque ({ads.filter(a => a.active).length})
                </h3>
                {ads.filter(a => a.active).length === 0 ? (
                  <p className="text-gray-400 text-sm">Nenhum produto em destaque</p>
                ) : (
                  <div className="space-y-3">
                    {ads.filter(a => a.active).map(ad => (
                      <div key={ad.id} className="bg-gray-700/50 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{ad.product_name}</p>
                            <p className="text-sm text-gray-400">{ad.store_name}</p>
                          </div>
                          <span className="text-xs px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded">
                            R$ {ad.price_paid?.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Até {new Date(ad.end_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Lista de Lojas para Gerenciar */}
              <div className="bg-gray-800 rounded-xl p-4">
                <h3 className="font-medium text-blue-400 mb-4 flex items-center gap-2">
                  <FiEdit /> Gerenciar Mensalidades por Loja
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {stores.map(store => (
                    <div key={store.id} className="bg-gray-700/50 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{store.name}</p>
                        <p className="text-xs text-gray-400">
                          {store.is_premium ? '⭐ Premium' : 'Plano Básico'}
                          {' | '}
                          Mensalidade: R$ {store.monthly_fee_custom?.toFixed(2) || settings?.monthly_fee?.toFixed(2) || '149,00'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenBillingModal(store)}
                        className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/40 text-sm flex items-center gap-1"
                      >
                        <FiEdit className="text-xs" /> Editar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mediation Tab - Chat History for Admin */}
          {activeTab === 'mediation' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">🛡️ Central de Mediação</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Buscar por nome, email ou pedido..."
                    value={chatSearch}
                    onChange={(e) => setChatSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && loadChatConversations(chatSearch)}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm w-64"
                  />
                  <button
                    onClick={() => loadChatConversations(chatSearch)}
                    className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Buscar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Lista de Conversas */}
                <div className="bg-gray-800 rounded-xl p-4">
                  <h3 className="font-medium text-blue-400 mb-4">💬 Conversas ({chatConversations.length})</h3>
                  
                  {chatConversations.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Nenhuma conversa encontrada</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {chatConversations.map(conv => (
                        <div
                          key={conv.order_id}
                          onClick={() => loadChatMessages(conv.order_id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedChatOrder === conv.order_id 
                              ? 'bg-blue-600/30 border border-blue-500' 
                              : 'bg-gray-700/50 hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-medium text-sm">#{conv.order_id.substring(0, 8)}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                conv.order_type === 'express' ? 'bg-purple-600/30 text-purple-400' : 'bg-green-600/30 text-green-400'
                              }`}>
                                {conv.order_type === 'express' ? 'Express' : 'Marketplace'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">{conv.message_count} msgs</span>
                          </div>
                          
                          <p className="text-xs text-gray-400 mb-2 truncate">
                            <span className="text-gray-500">{conv.last_message?.sender_name}:</span> {conv.last_message?.content}
                          </p>
                          
                          <div className="flex flex-wrap gap-1">
                            {conv.participants?.map((p, i) => (
                              <span key={i} className={`text-xs px-2 py-0.5 rounded ${
                                p.type === 'cliente' ? 'bg-blue-600/20 text-blue-400' :
                                p.type === 'lojista' ? 'bg-green-600/20 text-green-400' :
                                'bg-orange-600/20 text-orange-400'
                              }`}>
                                {p.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Visualizador de Mensagens */}
                <div className="bg-gray-800 rounded-xl p-4">
                  <h3 className="font-medium text-yellow-400 mb-4">📜 Histórico de Mensagens</h3>
                  
                  {!selectedChatOrder ? (
                    <div className="text-center py-16 text-gray-400">
                      <FiActivity className="text-4xl mx-auto mb-4 opacity-50" />
                      <p>Selecione uma conversa para ver o histórico</p>
                    </div>
                  ) : loadingChat ? (
                    <div className="text-center py-16">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : (
                    <>
                      {/* Participants Info */}
                      <div className="bg-gray-700/30 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-400 mb-2">Participantes:</p>
                        <div className="space-y-1">
                          {Object.values(chatParticipants).map((p, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className={`${
                                p.type === 'cliente' ? 'text-blue-400' :
                                p.type === 'lojista' ? 'text-green-400' :
                                'text-orange-400'
                              }`}>
                                {p.type === 'lojista' ? `🏪 ${p.store_name}` : p.type === 'entregador' ? `🛵 ${p.name}` : `👤 ${p.name}`}
                              </span>
                              <span className="text-gray-500 text-xs">{p.email}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="space-y-3 max-h-[300px] overflow-y-auto mb-4">
                        {chatMessages.map(msg => (
                          <div key={msg.id} className={`p-3 rounded-lg ${
                            msg.sender_type === 'admin' ? 'bg-purple-600/20 border border-purple-500' :
                            msg.sender_type === 'cliente' ? 'bg-blue-600/10' :
                            msg.sender_type === 'lojista' ? 'bg-green-600/10' :
                            'bg-orange-600/10'
                          }`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className={`text-xs font-medium ${
                                msg.sender_type === 'admin' ? 'text-purple-400' :
                                msg.sender_type === 'cliente' ? 'text-blue-400' :
                                msg.sender_type === 'lojista' ? 'text-green-400' :
                                'text-orange-400'
                              }`}>
                                {msg.sender_type === 'admin' ? '🛡️ ' : ''}{msg.sender_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(msg.created_at).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-200">{msg.content}</p>
                          </div>
                        ))}
                      </div>

                      {/* Send Mediation Message */}
                      <div className="border-t border-gray-700 pt-4">
                        <p className="text-xs text-gray-400 mb-2">✉️ Enviar mensagem de mediação (aparecerá como "Suporte BarueriFlash"):</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={mediationMessage}
                            onChange={(e) => setMediationMessage(e.target.value)}
                            placeholder="Digite sua mensagem de mediação..."
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && sendMediationMessage()}
                          />
                          <button
                            onClick={sendMediationMessage}
                            disabled={sendingMediation || !mediationMessage.trim()}
                            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                          >
                            {sendingMediation ? '...' : 'Enviar'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && settings && (
            <div className="max-w-xl space-y-4 animate-fadeIn">
              <h2 className="text-base lg:text-lg font-semibold">Configurações</h2>
              
              {/* Primeiro Mês Grátis */}
              <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-green-400">🎉 Primeiro Mês Grátis</h3>
                    <p className="text-xs text-gray-400">Promoção de lançamento para novos lojistas</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.first_month_free || false}
                      onChange={e => setSettings({...settings, first_month_free: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-[#DC143C]">Comissão por Venda (3 Faixas)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Faixa 1 - Até (R$)</label>
                    <input
                      type="number"
                      value={settings.commission_threshold_low}
                      onChange={e => setSettings({...settings, commission_threshold_low: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Taxa Faixa 1 (R$)</label>
                    <input
                      type="number"
                      value={settings.commission_fee_low}
                      onChange={e => setSettings({...settings, commission_fee_low: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Faixa 2 - Até (R$)</label>
                    <input
                      type="number"
                      value={settings.commission_threshold_high}
                      onChange={e => setSettings({...settings, commission_threshold_high: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Taxa Faixa 2 (R$)</label>
                    <input
                      type="number"
                      value={settings.commission_fee_medium}
                      onChange={e => setSettings({...settings, commission_fee_medium: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Taxa Faixa 3 - Acima (R$)</label>
                  <input
                    type="number"
                    value={settings.commission_fee_high}
                    onChange={e => setSettings({...settings, commission_fee_high: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-green-400">Taxa de Entrega</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Total</label>
                    <input
                      type="number"
                      value={settings.delivery_fee_total}
                      onChange={e => setSettings({...settings, delivery_fee_total: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Entregador</label>
                    <input
                      type="number"
                      value={settings.delivery_fee_driver}
                      onChange={e => setSettings({...settings, delivery_fee_driver: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Plataforma</label>
                    <input
                      type="number"
                      value={settings.delivery_fee_platform}
                      onChange={e => setSettings({...settings, delivery_fee_platform: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-yellow-400">Preços de ADS (Produtos em Destaque)</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Por Dia (R$)</label>
                    <input
                      type="number"
                      value={settings.ads_price_per_day || 5}
                      onChange={e => setSettings({...settings, ads_price_per_day: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Por Semana (R$)</label>
                    <input
                      type="number"
                      value={settings.ads_price_per_week || 25}
                      onChange={e => setSettings({...settings, ads_price_per_week: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Por Mês (R$)</label>
                    <input
                      type="number"
                      value={settings.ads_price_per_month || 80}
                      onChange={e => setSettings({...settings, ads_price_per_month: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="w-full py-3 bg-[#DC143C] rounded-xl font-semibold hover:bg-[#B01030] disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          )}

          {/* Modal de Nova Promoção */}
          {showPromotionModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
                <h3 className="text-lg font-semibold mb-4">Nova Promoção</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Loja</label>
                    <select
                      value={newPromotion.store_id}
                      onChange={e => setNewPromotion({...newPromotion, store_id: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    >
                      <option value="">Selecione uma loja</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Tipo de Desconto</label>
                      <select
                        value={newPromotion.discount_type}
                        onChange={e => setNewPromotion({...newPromotion, discount_type: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                      >
                        <option value="percentage">Percentual (%)</option>
                        <option value="fixed">Valor Fixo (R$)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Valor</label>
                      <input
                        type="number"
                        value={newPromotion.discount_value}
                        onChange={e => setNewPromotion({...newPromotion, discount_value: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Aplicar em</label>
                    <select
                      value={newPromotion.applies_to}
                      onChange={e => setNewPromotion({...newPromotion, applies_to: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    >
                      <option value="monthly_fee">Mensalidade</option>
                      <option value="commission">Comissão por Venda</option>
                      <option value="both">Ambos</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Data de Término</label>
                    <input
                      type="date"
                      value={newPromotion.end_date}
                      onChange={e => setNewPromotion({...newPromotion, end_date: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Motivo (opcional)</label>
                    <input
                      type="text"
                      value={newPromotion.reason}
                      onChange={e => setNewPromotion({...newPromotion, reason: e.target.value})}
                      placeholder="Ex: Lançamento, Fidelidade..."
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowPromotionModal(false)}
                    className="flex-1 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreatePromotion}
                    className="flex-1 py-2 bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    Criar Promoção
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Editar Cobrança da Loja */}
          {showBillingModal && selectedStoreBilling && billingData && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
                <h3 className="text-lg font-semibold mb-2">Cobrança - {selectedStoreBilling.name}</h3>
                <p className="text-xs text-gray-400 mb-4">Criada há {billingData.days_since_creation} dias</p>
                
                {billingData.first_month_free && (
                  <div className="bg-green-600/20 text-green-400 rounded-lg p-3 mb-4 text-sm">
                    🎉 Primeiro mês grátis ativo! A cobrança inicia em {new Date(billingData.next_billing_date).toLocaleDateString('pt-BR')}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Mensalidade Personalizada (deixe vazio para usar padrão R$ {settings?.monthly_fee || 149})
                    </label>
                    <input
                      type="number"
                      value={billingData.monthly_fee_custom || ''}
                      onChange={e => setBillingData({...billingData, monthly_fee_custom: e.target.value ? parseFloat(e.target.value) : null})}
                      placeholder={`R$ ${settings?.monthly_fee || 149} (padrão)`}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3">
                    <div>
                      <p className="font-medium">Plano Premium</p>
                      <p className="text-xs text-gray-400">Permite criar anúncios de produtos</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={billingData.is_premium || false}
                        onChange={e => setBillingData({...billingData, is_premium: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                  
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-1">Mensalidade Efetiva:</p>
                    <p className="text-xl font-bold text-green-400">R$ {billingData.effective_monthly_fee?.toFixed(2)}</p>
                    {billingData.active_promotions?.length > 0 && (
                      <p className="text-xs text-yellow-400 mt-1">
                        * Com desconto de promoção ativa
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowBillingModal(false);
                      setSelectedStoreBilling(null);
                      setBillingData(null);
                    }}
                    className="flex-1 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleUpdateBilling(selectedStoreBilling.id, {
                      monthly_fee_custom: billingData.monthly_fee_custom,
                      is_premium: billingData.is_premium
                    })}
                    className="flex-1 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
